import { CogIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useRef, useState } from "react";

import { ModelParameters } from "@/components/ModelParameters";
import { useBlock } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PromptTemplateConfiguration } from "@/types/evaluate";
import { PromptBlueprint } from "@/types";

interface EngineConfigurationProps {
  promptData?: Partial<PromptBlueprint>;
  defaultPromptData?: Partial<PromptBlueprint>;
  isLoading: boolean;
  onUpdate: (promptData?: Partial<PromptBlueprint>) => void;
  isChat?: boolean;
  messages?: string[];
}

const EngineConfiguration = ({
  promptData,
  defaultPromptData,
  isLoading,
  onUpdate,
  isChat,
  messages,
}: EngineConfigurationProps) => {
  const isReadonly = useIsReadonly();
  const block = useBlock();
  const [open, setOpen] = useState(false);
  const [interimPromptData, setInterimPromptData] = useState<Partial<PromptBlueprint> | undefined>(
    promptData,
  );
  const [isUsingDefault, setIsUsingDefault] = useState(
    !(block.configuration as PromptTemplateConfiguration)?.engine,
  );

  const prevInterimPromptData = useRef(interimPromptData);

  const isCustom = !isUsingDefault;

  const hasInterimChanges = useMemo(() => {
    if (!defaultPromptData || !interimPromptData) {
      return false;
    }
    return JSON.stringify(interimPromptData) !== JSON.stringify(defaultPromptData);
  }, [interimPromptData, defaultPromptData]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        setInterimPromptData(promptData);
        setIsUsingDefault(!(block.configuration as PromptTemplateConfiguration)?.engine);
        prevInterimPromptData.current = promptData;
      } else if (
        JSON.stringify(prevInterimPromptData.current) !== JSON.stringify(interimPromptData)
      ) {
        prevInterimPromptData.current = interimPromptData;
        onUpdate(isUsingDefault ? undefined : interimPromptData);
      }
      setOpen(newOpen);
    },
    [promptData, block.configuration, interimPromptData, isUsingDefault, onUpdate],
  );

  const handleResetToDefault = useCallback(() => {
    setInterimPromptData(defaultPromptData);
    setIsUsingDefault(true);
  }, [defaultPromptData]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const areEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) {
      return true;
    }
    if (
      (obj1 === null || obj1 === undefined || !Object.keys(obj1).length) !==
      (obj2 === null || obj2 === undefined || !Object.keys(obj2).length)
    ) {
      return false;
    }
    if (obj1 === null && obj2 === null) {
      return true;
    }
    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
      return obj1 === obj2;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    return keys1.every(key => areEqual(obj1[key], obj2[key]));
  }, []);

  const handleModelParametersUpdate = useCallback(
    (newData: Partial<PromptBlueprint>) => {
      if (!areEqual(interimPromptData, newData)) {
        setInterimPromptData(newData);
        setIsUsingDefault(false);
      }
    },
    [interimPromptData, areEqual],
  );

  if (!promptData || isLoading) {
    return (
      <div className="flex items-center justify-between">
        <Skeleton width={120} height={24} borderRadius={12} />
        <Skeleton width={140} height={36} />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      {isCustom ? (
        <span className="text-sm text-blue-600">
          {promptData.metadata?.model?.name ?? "Custom configuration"}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">
          {defaultPromptData?.metadata?.model?.name
            ? defaultPromptData?.metadata?.model?.name + " (Default)"
            : "Default configuration"}
        </span>
      )}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <CogIcon className="mr-1.5 h-4 w-4" /> {isReadonly ? "View" : "Configure"} Parameters
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Model Parameters</DialogTitle>
            <DialogDescription>
              {isCustom
                ? "You are using custom model configuration settings."
                : "You are using default model configuration settings."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ModelParameters
              key={String(isUsingDefault)}
              className="w-full justify-between"
              isPromptChat={!!isChat}
              displayAsPopover={false}
              shouldShowClearButton={false}
              promptData={interimPromptData}
              readonly={isReadonly}
              onUpdate={handleModelParametersUpdate}
              messages={messages}
            />
          </div>
          {!isReadonly && !isUsingDefault && (
            <DialogFooter>
              <Button
                onClick={handleResetToDefault}
                variant="outline"
                disabled={!hasInterimChanges || isReadonly}
              >
                Reset to Default
              </Button>
              <Button
                onClick={() => {
                  handleOpenChange(false);
                  setOpen(false);
                }}
              >
                Save
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default observer(EngineConfiguration);
